import { CompanyModel, TranslationLookups } from 'api/model';
import { useCompanyUpdateCompany } from 'api/company/company';
import Button from 'components/inputs/Button';
import SearchableSelect, {
  SelectOption,
} from 'components/inputs/SearchableSelect';
import Table from 'components/Table';
import { ColumnSetting, makeColumnClassName } from 'components/Table/utils';
import useTranslations from 'contexts/basicData/useTranslations';
import useCompanies, {
  useRefreshCompaniesCall,
} from 'contexts/basicData/useCompanies';
import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import { makeStringComparator } from 'utils/sorting';
import { showException } from 'utils/exception-helper';

const Wrapper = styled.div``;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 50px;
  padding: 10px;
  gap: 10px;

  button {
    padding: 0 10px;
    height: 100%;
  }
`;

const Intro = styled.p`
  margin: 10px;
  max-width: 70ch;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

/** Amber, inte rött: punkten är inte felaktig, den är oanvändbar tills koordinaten finns. */
const MissingCoordinate = styled.span`
  color: rgb(138, 98, 6);
  font-weight: 600;
`;

const MyTable = styled(Table)`
  .${makeColumnClassName(0)} {
    padding-left: 10px;
  }
` as typeof Table;

const hasCoordinate = (company: CompanyModel) =>
  company.latitude != null && company.longitude != null;

const DriverSwapPointsSettingsPage: FC = () => {
  const translations = useTranslations();
  const { allCompanies } = useCompanies();
  const refreshCompanies = useRefreshCompaniesCall();
  const updateCompany = useCompanyUpdateCompany();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );

  const swapPoints = useMemo(
    () =>
      allCompanies
        .filter((c) => c.isDriverSwapPoint === true)
        .sort(makeStringComparator((c) => c.name)),
    [allCompanies]
  );

  const addableOptions = useMemo((): SelectOption<number | null>[] => {
    const options: SelectOption<number | null>[] = allCompanies
      .filter((c) => c.active && c.isDriverSwapPoint !== true)
      .sort(makeStringComparator((c) => c.name))
      .map((c) => ({
        label: c.city ? `${c.name} — ${c.city}` : c.name,
        value: c.companyID,
        description: hasCoordinate(c) ? undefined : 'saknar koordinat',
      }));
    options.unshift({ label: 'Välj företag att lägga till', value: null });
    return options;
  }, [allCompanies]);

  // Hela företagsmodellen måste skickas tillbaka: CompanyLogic.UpdateCompany rensar
  // prisrader som inte finns med i modellen, så en delvis modell raderar företagets
  // transportpriser. Modellerna från useCompanies bär prisraderna (GetCompanies
  // inkluderar dem), så en rundtur med bara flaggan ändrad är säker.
  const setFlag = async (company: CompanyModel, isSwapPoint: boolean) => {
    try {
      await updateCompany.mutateAsync({
        data: { ...company, isDriverSwapPoint: isSwapPoint },
      });
      await refreshCompanies.run();
    } catch (error) {
      showException(error);
    }
  };

  const columnSettings: ColumnSetting<CompanyModel, TranslationLookups>[] = [
    {
      head: 'Nr',
      render: (c, rp, focused, index) => index + 1,
      width: 10,
    },
    {
      head: 'Namn',
      render: (c) => c.name,
      width: 140,
      sortFunction: makeStringComparator((c) => c.name),
    },
    {
      head: 'Adress',
      render: (c) => c.address,
      width: 140,
    },
    {
      head: 'Ort',
      render: (c) => c.city,
      width: 90,
      sortFunction: makeStringComparator((c) => c.city),
    },
    {
      head: 'Koordinat',
      render: (c) =>
        hasCoordinate(c) ? (
          'Ja'
        ) : (
          <MissingCoordinate>Saknas – kan inte föreslås</MissingCoordinate>
        ),
      width: 130,
    },
    {
      head: 'Aktiv',
      render: (c) => (c.active ? 'Ja' : 'Nej'),
      width: 50,
    },
    {
      head: '',
      render: (c) => (
        <Button onClick={() => setFlag(c, false)}>Ta bort</Button>
      ),
      width: 80,
    },
  ];

  const selectedCompany = allCompanies.find(
    (c) => c.companyID === selectedCompanyId
  );

  return (
    <Wrapper>
      <Intro>
        Skiftespunkter är de adresser ruttplaneringen får föreslå för ett skifte:
        två motriktade långrutter möts halvvägs, förarna skiftar fordon och båda
        kommer hem samma dag. Mängden är liten och kurerad — historiken visar att
        skiftena sker på ett fåtal bensinstationer och hubbar längs E4 och E20. En
        punkt utan koordinat kan aldrig föreslås.
      </Intro>
      <Actions>
        <SearchableSelect
          options={addableOptions}
          selectedValue={selectedCompanyId}
          onOptionClicked={(value: number | null) => {
            setSelectedCompanyId(value);
          }}
          searchFilter={(searchString, opts) => {
            const needle = searchString.toLowerCase();
            return opts.filter((opt) =>
              typeof opt.label === 'string'
                ? opt.label.toLowerCase().includes(needle)
                : false
            );
          }}
        />
        <Button
          disabled={!selectedCompany}
          onClick={() => {
            if (!selectedCompany) return;
            setFlag(selectedCompany, true);
            setSelectedCompanyId(null);
          }}
        >
          Lägg till som skiftespunkt
        </Button>
      </Actions>
      <MyTable
        columnSettings={columnSettings}
        renderProps={translations as TranslationLookups}
        rows={swapPoints}
        useColumnWidthAsFlexAmount
      />
    </Wrapper>
  );
};

export default DriverSwapPointsSettingsPage;
